import React, { useEffect, useState } from 'react';
import { Button, Input, Layout, Space } from 'antd';
import { CloseOutlined, DeleteFilled, EditFilled, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import { Responsive, WidthProvider } from 'react-grid-layout';
// import 'react-grid-layout/css/styles.css';
import './dashboard.less';
import Page from 'components/Page';
import ResultsConfig from 'containers/Tools/CustomResults/ResultsConfig';
import DrawerForm from 'components/DrawerForm';
import { gql } from '@apollo/client';
import { useMutation, useQuery } from 'hooks/apollo';
import GlobalFilter from 'components/GlobalFilter/GlobalFilter';
import { DashboardContentType } from 'shared/services/prisma/types';
import useGlobalContext from 'hooks/useGlobalContext';
import { useHistory } from 'react-router-dom';
import { useKeyPress, useSetState } from 'ahooks';
import { EDIT_DASHBOARD } from 'shared/constants/permissions';
import useUser from 'hooks/useUser';
import DataSourceSelect from 'components/DataSourceSelect';
import DashboardContent from './DashboardContent';
import EditDashboardContent from './EditDashboardContent';

const contentFragment = gql`
  fragment Content on DashboardContent {
    id
    title
    type
    layout
    resultsConfig {
      id
      metrics
      dimensions
      filter
      settings
    }
  }
`;

const QUERY = gql`
  query Dashboard($id: ID!) {
    dashboard(id: $id) {
      id
      title
      contents {
        ...Content        
      }
    }    
  }
  ${contentFragment}
`;

const UPDATE_DASHBOARD_MUTATION = gql`
  mutation UpdateDashboard($id: ID!, $input: DashboardInput!) {
    updateDashboard(id: $id, input: $input) {
      id
      title
      contents {
        ...Content
      }
    }    
  }
  ${contentFragment}
`;

const CREATE_DASHBOARD_CONTENT_MUTATION = gql`
  mutation CreateDashboardContent($dashboardId: ID!, $input: DashboardContentInput!) {
    createDashboardContent(dashboardId: $dashboardId, input: $input) {
      id
    }
  }
`;

const UPDATE_DASHBOARD_CONTENT_MUTATION = gql`
  mutation UpdateDashboardContent($id: ID!, $input: DashboardContentInput!) {
    updateDashboardContent(id: $id, input: $input) {
      ...Content
    }
  }
  ${contentFragment}
`;

const DELETE_DASHBOARD_MUTATION = gql`
  mutation DeleteDashboard($id: ID!) {
    deleteDashboard(id: $id) {
      id
    }
  }
`;

const DELETE_DASHBOARD_CONTENT_MUTATION = gql`
  mutation DeleteDashboardContent($id: ID!) {
    deleteDashboardContent(id: $id) {
      id
    }
  }
`;

const GridLayout = WidthProvider(Responsive);

const rowHeight = 20;

const getInitialResultsConfig = () => ({
  metrics: [{
    table: 'matomoVisits',
    field: 'visitId',
    aggregation: 'uniqExact',
    name: 'visits',
    label: 'Visits',
    displayConfig: {
      visible: true,
      columns: ['value'],
      format: 'number',
    },
  }],
  settings: {
    rowLimit: 100,
    pageLimit: 5,
  },
});

const createContent = ({ y, title, resultsConfig }) => ({
  title,
  type: DashboardContentType.Result,
  layout: {
    x: 0,
    y,
    w: 4,
    h: 10,
    minW: 1,
    maxW: 4,
    minH: 1,
  },
  resultsConfig: resultsConfig || getInitialResultsConfig(),
});

const breakpoints = {
  lg: 0,
};

const cols = {
  lg: 4,
};

const getContentLayout = (layout, contentId) => layout.lg?.find(l => l.i === contentId);

const getDashboardInput = (content, layout, contentTitles) => content.map(c => {
  const contentLayout = getContentLayout(layout, c.id);

  return {
    id: c.id,
    title: contentTitles[c.id],
    layout: contentLayout,
  };
});

const Dashboard = ({ dashboardId }) => {
  const { hasPermission } = useUser();
  const [globalFilter, setGlobalFilter] = useState();
  const { data, loading, refetch } = useQuery(QUERY, {
    variables: { id: dashboardId },
  });
  const [updateDashboard, { loading: updating }] = useMutation(UPDATE_DASHBOARD_MUTATION);
  const [deleteDashboard] = useMutation(DELETE_DASHBOARD_MUTATION);
  const [createDashboardContent, { loading: creating }] = useMutation(CREATE_DASHBOARD_CONTENT_MUTATION);
  const [updateDashboardContent] = useMutation(UPDATE_DASHBOARD_CONTENT_MUTATION);
  const [deleteDashboardContent, { loading: removing }] = useMutation(DELETE_DASHBOARD_CONTENT_MUTATION);

  const { modal, message } = useGlobalContext();

  const history = useHistory();
  const [layout, setLayout] = useSetState({});
  const [editDashboard, setEditDashboard] = useState(false);
  const [editContentQuery, setEditContentQuery] = useState(null);
  const [title, setTitle] = useState('');
  const [contentTitles, setContentTitles] = useState({});
  const [dataSourceIds, setDataSourceIds] = useState([]);

  const dashboard = data?.dashboard;
  const content = dashboard?.contents || [];

  const canEdit = hasPermission(EDIT_DASHBOARD);

  useEffect(() => {
    setTitle(dashboard?.title);
    setContentTitles(content.reduce((acc, c) => ({ ...acc, [c.id]: c.title }), {}));
  }, [dashboard]);

  useEffect(() => {
    if (content.length) {
      setLayout({
        lg: content.map((c) => {
          const contentLayout = getContentLayout(layout, c.id) || c.layout;

          return {
            i: c.id || c.i,
            ...contentLayout,
            h: editDashboard ? 5 : contentLayout.h,
            isDraggable: editDashboard,
            isResizable: editDashboard,
          };
        }),
      });
    }
  }, [content, editDashboard]);

  useKeyPress('ctrl.v', async () => {
    const text = await navigator.clipboard.readText();
    try {
      const clipboardData = JSON.parse(text);
      const input = createContent({
        title: `Copy of ${clipboardData.title}`,
        y: layout.lg?.length ? Math.max(...layout.lg.map(l => l.y)) + 1 : 0,
        resultsConfig: clipboardData.resultsConfig,
      });
      await createDashboardContent({ variables: { dashboardId, input } });
      refetch();
    } catch (e) {
      message.error('Clipboard content is not a valid results config');
      console.error(e);
    }
  });

  const onSaveDashboard = async () => {
    await updateDashboard({
      variables: {
        id: dashboardId,
        input: {
          title,
          contents: getDashboardInput(content, layout, contentTitles),
        },
      },
    });
    setEditDashboard(false);
  };

  const onDeleteDashboard = async () => {
    modal.confirm({
      title: 'Are you sure you want to delete this dashboard?',
      onOk: async () => {
        await deleteDashboard({ variables: { id: dashboardId } });
        history.push('/tools/dashboards');
      },
    });
  };

  const onAddContent = async () => {
    const input = createContent({
      title: `Result ${content.length + 1}`,
      y: layout.lg?.length ? Math.max(...layout.lg.map(l => l.y)) + 1 : 0,
    });
    await createDashboardContent({ variables: { dashboardId, input } });
    refetch();
  };

  const onLayoutChange = (newLayout) => {
    setLayout({ lg: newLayout });
  };

  const onEditContentQuerySubmit = async (values) => {
    await updateDashboardContent({
      variables: {
        id: editContentQuery.id,
        input: { resultsConfig: values },
      },
    });
    setEditContentQuery(null);
  };

  const removeContent = async (id) => {
    if (id) {
      await deleteDashboardContent({ variables: { id } });
      refetch();
    }
  };

  const onContentSizeChange = (id, size) => {
    if (size?.height) {
      setLayout(prev => ({
        lg: prev.lg.map(l => (l.i === id ? {
          ...l,
          h: Math.ceil((size.height + 10) / (rowHeight + 10)),
        } : l)),
      }));
    }
  };

  return (
    <Layout>
      <Page loading={loading}>
        {canEdit ? (
          <Page.Header
            title={editDashboard ? <Input style={{ width: 350 }} value={title} onChange={(e) => setTitle(e.target.value)} /> : dashboard?.title}
            extra={editDashboard ? (
              <Space>
                <Button
                  icon={<CloseOutlined />}
                  onClick={() => setEditDashboard(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={onSaveDashboard}
                  loading={updating}
                >
                  Save
                </Button>
                <Button
                  icon={<DeleteFilled />}
                  onClick={onDeleteDashboard}
                  danger
                />
              </Space>
            ) : (
              <Space>
                <DataSourceSelect value={dataSourceIds} onChange={setDataSourceIds} multiple allowClear />
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={onAddContent}
                  loading={creating}
                >
                  Add content
                </Button>
                <Button
                  icon={<EditFilled />}
                  onClick={() => setEditDashboard(true)}
                >
                  Edit
                </Button>
              </Space>
            )}
          />
        ) : (
          <Page.Header
            title={dashboard?.title}
          />
        )}

        <GridLayout
          layouts={layout}
          onLayoutChange={onLayoutChange}
          compactType="vertical"
          draggableHandle=".drag-handle"
          breakpoints={breakpoints}
          cols={cols}
          rowHeight={rowHeight}
          resizeHandles={['e']}
          useCSSTransforms
          containerPadding={[0, 0]}
        >
          {content.map((c) => (
            <div key={c.id || c.i}>
              {editDashboard ? (
                <EditDashboardContent
                  content={c}
                  removeContent={removeContent}
                  removing={removing}
                  contentTitles={contentTitles}
                  setContentTitles={setContentTitles}
                />
              ) : (
                <DashboardContent
                  content={c}
                  globalFilter={globalFilter}
                  dataSourceIds={dataSourceIds}
                  removeContent={removeContent}
                  removing={removing}
                  setEditContentQuery={canEdit ? setEditContentQuery : null}
                  onSizeChange={onContentSizeChange}
                />
              )}
            </div>
          ))}
        </GridLayout>
        {canEdit && (
        <DrawerForm
          title="Result settings"
          open={!!editContentQuery}
          onCancel={() => setEditContentQuery(null)}
          onSubmit={onEditContentQuerySubmit}
          initialValues={editContentQuery?.resultsConfig}
        >
          <ResultsConfig />
        </DrawerForm>
        )}
      </Page>
      <GlobalFilter
        onChange={setGlobalFilter}
      />
    </Layout>
  );
};

export default Dashboard;
